import React, { useEffect, useState, useContext } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AuthContext } from '../../context/AuthContext';
import './Reservation.css';

const API_URL = '/api/resources';

const buildTree = (list, parentId = 0, allowedCategory = 'all') => {
  return list
    .filter(item => {
      if (item.parent_id !== parentId) return false;
      if (item.res_type === '카테고리') {
        // 카테고리는 조건에 맞는 하위 자원이 있을 때만 표시
        const hasValidChildren = list.some(child => 
          child.parent_id === item.id &&
          (child.res_type === '카테고리' || 
           (allowedCategory === 'all' || child.res_category === allowedCategory))
        );
        return hasValidChildren;
      }
      return true;
    })
    .map(item => ({ ...item, children: buildTree(list, item.id, allowedCategory) }));
};

function ResourcePicker({ selType = 'def', allowedCategory = 'all', onSelect, onClose }) {
  const [resources, setResources] = useState([]);
  const [expandedItems, setExpandedItems] = useState(['root']);
  const { token } = useContext(AuthContext);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const res = await axios.get(API_URL, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = Array.isArray(res.data) ? res.data : res.data.data;
        
        // ✅ 카테고리는 항상 표시, 자원은 use_yn=Y 이고 allowedCategory 에 맞는 것만 표시
        const filtered = (data || []).filter(r => {
          if (r.res_type === '카테고리') return true;
          if (r.use_yn !== 'Y') return false;
          if (allowedCategory !== 'all') return r.res_category === allowedCategory;
          return true;
        });
        setResources(filtered);

        // ✅ 처음 열릴 때 전체 펼치기
        if (filtered && Array.isArray(filtered)) {
          const allIds = ['root', ...filtered.map(r => String(r.id))];
          setExpandedItems(allIds);
        }
      } catch (err) {
        console.error('자원 목록 불러오기 실패:', err);
      }
    };
    if (token) fetchResources();
  }, [token, allowedCategory]);

  const treeData = buildTree(resources, 0, allowedCategory);

  const renderTree = nodes =>
    nodes.map(node => (
      <TreeItem
        key={node.id}
        itemId={String(node.id)}
        collapseIcon={<ExpandMoreIcon />}
        expandIcon={<ChevronRightIcon />}
        label={
          <div
            className={node.res_type === '카테고리' ? "picker-category-label" : "picker-resource-box"}
            style={node.res_type === '자원'
              ? { backgroundColor: node.res_color, color: '#fff' }
              : {}
            }
            onClick={() => {
              if (node.res_type === '자원') onSelect(node);
            }}
          >
            {node.res_type === '카테고리' ? `📂 ${node.res_name}` : node.res_name}
          </div>
        }
      >
        {node.children && renderTree(node.children)}
      </TreeItem>
    ));

  // 전체 펼치기/접기 버튼 핸들러
  const expandAll = () => {
    const allIds = ['root', ...resources.map(r => String(r.id))];
    setExpandedItems(allIds);
  };
  const collapseAll = () => {
    setExpandedItems(['root']);
  };

  return ReactDOM.createPortal(
    <div className="picker-modal">
      <div className="picker-content">
        <div className="picker-body">
          {/* 헤더 영역 */}
          <div className="picker-header">
            <h5 className="picker-title" style={{ margin: 0, fontWeight: 'bold' }}>예약자원 선택</h5>
            <div className="picker-header-actions">
              <button className="picker-expand-btn" onClick={expandAll}>+</button>
              <button className="picker-expand-btn" onClick={collapseAll}>−</button>
            </div>
          </div>

          {/* 구분선 */}
          <hr className="picker-divider" />

          {/* 트리 영역 */}
          <div className="picker-tree-container">
            <SimpleTreeView
              expandedItems={expandedItems}
              onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
            >
              <TreeItem
                itemId="root"
                collapseIcon={<ExpandMoreIcon />}
                expandIcon={<ChevronRightIcon />}
                label={<span className="picker-tree-root-label" style={{ fontWeight: 'bold' }}>📂 예약자원</span>}
              >
                {renderTree(treeData)}
              </TreeItem>
            </SimpleTreeView>
          </div>
        </div>

        {/* 닫기 버튼 하단 오른쪽 고정 */}
        <div className="picker-footer">
          <button className="btn btn-secondary" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ResourcePicker;